import { infiniteQueryOptions } from "@tanstack/react-query"
import type {
  StoreSortBy,
  StoreSortOrder,
} from "@/components/e-commerce/store/query-params/contract"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"
import {
  DATA_FRESHNESS_MS,
  toReactQueryGcTime,
} from "@/utilities/data-freshness"

export interface StoreProductsPageResponse {
  items: WooCommerceProduct[]
  page: number
  totalPages: number
  totalProducts: number
}

export interface StoreProductsQuery {
  q?: string
  orderby: StoreSortBy
  order: StoreSortOrder
  per_page: number
}

interface StoreProductsPageLoaderInput extends StoreProductsQuery {
  page: number
}

export type StoreProductsPageLoader = (
  input: StoreProductsPageLoaderInput
) => Promise<StoreProductsPageResponse>

export const buildStoreProductsQueryKey = (queryKeySeed: string) =>
  ["store-products", queryKeySeed] as const

export const buildStoreProductsQuerySeed = ({
  storeId,
  storeSlug,
  query,
}: {
  storeId: number
  storeSlug: string
  query: StoreProductsQuery
}): string =>
  JSON.stringify({
    storeId,
    storeSlug,
    q: query.q,
    orderby: query.orderby,
    order: query.order,
    per_page: query.per_page,
  })

export const getStoreProductsInfiniteQueryOptions = ({
  onLoadMoreProducts,
  query,
  queryKeySeed,
}: {
  onLoadMoreProducts: StoreProductsPageLoader
  query: StoreProductsQuery
  queryKeySeed: string
}) =>
  infiniteQueryOptions({
    queryKey: buildStoreProductsQueryKey(queryKeySeed),
    staleTime: DATA_FRESHNESS_MS.storeList,
    gcTime: toReactQueryGcTime(DATA_FRESHNESS_MS.storeList),
    initialPageParam: 1,
    queryFn: async ({ pageParam }: { pageParam: number }) =>
      onLoadMoreProducts({
        page: pageParam,
        ...query,
      }),
    getNextPageParam: (lastPage: StoreProductsPageResponse) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
  })
